/* eslint-disable react/no-unescaped-entities */
/**
 * DossierWizard — Phase 5 « Parcours unique CONFILEX ».
 *
 * Parcours 6 étapes (verrouillé du début à la fin) :
 *   1. Décrivez votre situation + domaine
 *   2. Téléverser vos documents (optionnel — noms uniquement en MVP)
 *   3. Analyse IA Claude → résumé, chronologie, pièces manquantes, points de vigilance
 *   4. Services complémentaires (Structuration / Vérification / Audit 29-39-49€ / Poursuite gratuite)
 *   5. Choix du spécialiste (parmi les 3 spécialistes les plus compatibles)
 *   6. Transmission sécurisée (verrouillée tant que les étapes 1→5 ne sont pas validées)
 *
 * Le bouton "Transmettre" est strictement désactivé tant que :
 *   - L'analyse IA n'a pas été réalisée
 *   - Le choix services complémentaires n'a pas été confirmé (même "Gratuit")
 *   - Un spécialiste n'a pas été sélectionné
 *   - Si le spécialiste est NON ABONNÉ : le paiement 89€ doit avoir été effectué AVANT
 *
 * Persistance backend : POST /api/dossier/analyze → case_id
 *                       GET  /api/dossier/{id}/recipients?city=
 *                       POST /api/dossier/{id}/transmit
 *                       POST /api/dossier/{id}/audit/checkout (29/39/49€ optionnel)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import BookingNavbar from "@/components/booking/BookingNavbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import AuditLevelSelector from "@/components/booking/AuditLevelSelector";
import {
  ArrowRight, ArrowLeft, Sparkles, Upload, FileText, X, MapPin, Phone, Globe,
  CheckCircle2, AlertTriangle, Clock, Check, Send, Mail, User, Building2,
  ShieldCheck, ListChecks, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAINES = [
  "Droit du travail", "Droit des affaires", "Droit immobilier",
  "Droit fiscal", "Droit de la famille", "Succession",
  "Droit pénal", "Droit pénal des affaires", "Contentieux des affaires",
  "Corporate M&A", "Création de société", "Droit social",
];

const STEPS = [
  { n: 1, label: "Votre situation" },
  { n: 2, label: "Vos documents" },
  { n: 3, label: "Analyse IA" },
  { n: 4, label: "Services complémentaires" },
  { n: 5, label: "Choix du spécialiste" },
  { n: 6, label: "Transmission" },
];


export default function DossierWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);

  // Form state
  const [description, setDescription] = useState("");
  const [domaine, setDomaine] = useState("");
  const [city, setCity] = useState("");
  const [documents, setDocuments] = useState([]); // [{name, size, text?}]
  const [userInfo, setUserInfo] = useState({ name: "", email: "", phone: "" });

  // Server-side state
  const [caseData, setCaseData] = useState(null); // résultat /analyze
  const [recipients, setRecipients] = useState([]);
  const [qualification, setQualification] = useState(null); // { required: bool, missing: [], guidance: str }
  const [pickedExpert, setPickedExpert] = useState(null);
  const [transmission, setTransmission] = useState(null);

  // Étape 4 — Services complémentaires (Parcours unique v3.43)
  // Tant que `complementaryServiceChosen` est false, l'étape 5 (choix spécialiste)
  // reste inaccessible : c'est le verrou structurel du parcours unique CONFILEX.
  // Valeurs possibles : null | "free" | "audit_29" | "audit_39" | "audit_49"
  const [complementaryService, setComplementaryService] = useState(null);
  const complementaryServiceChosen = complementaryService !== null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length));
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  // STEP 1 → 2 : pas d'API
  // STEP 2 → 3 : déclenche l'analyse IA
  const runAnalyze = async () => {
    setError(null);
    setLoading(true);
    try {
      const payload = {
        description,
        domaine: domaine || undefined,
        city: city || undefined,
        document_names: documents.map((d) => d.name),
        user_email: userInfo.email || undefined,
      };
      const r = await axios.post(`${API}/dossier/analyze`, payload);
      setCaseData(r.data);
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de l'analyse. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  // STEP 3 → 4 : passage manuel à l'étape services complémentaires
  const goToComplementary = () => {
    if (!caseData?.id) return;
    setStep(4);
  };

  // v3.44 — Basculement anti-impasse entre "abonnés" et "référencés"
  // Le parcours démarre en pool="subscribed" (gratuit). Si aucun résultat +
  // has_fallback=true, l'utilisateur peut cliquer un bouton pour basculer vers
  // pool="referenced" (payant) sans jamais réinitialiser sa recherche.
  const [expertPool, setExpertPool] = useState("subscribed"); // "subscribed" | "referenced"
  const [poolHasFallback, setPoolHasFallback] = useState(false);

  // STEP 4 → 5 : charge les spécialistes recommandés (seulement après choix services)
  const loadRecipients = async (poolOverride = null) => {
    if (!caseData?.id || !complementaryServiceChosen) return;
    // Garde-fou UX : intercepter en amont si description trop courte
    // (évite un aller-retour serveur inutile et redirige l'utilisateur direct au Step 1).
    if (description.trim().length < MIN_DESCRIPTION_CHARS) {
      toast.error(
        `Votre description est trop courte (${description.trim().length}/${MIN_DESCRIPTION_CHARS} caractères). Complétez-la à l'étape 1.`,
      );
      setStep(1);
      return;
    }
    const currentPool = poolOverride || expertPool;
    setError(null);
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dossier/${caseData.id}/recipients`, {
        params: { city: city || undefined, limit: 6, pool: currentPool },
      });
      setRecipients(r.data?.recipients || []);
      setPoolHasFallback(!!r.data?.has_fallback);
      if (poolOverride) setExpertPool(poolOverride);
      if (r.data?.qualification_required) {
        setQualification({
          required: true,
          missing: r.data?.missing_fields || [],
          guidance: r.data?.guidance || "Complétez votre dossier.",
          specialty: r.data?.specialty,
        });
      } else {
        setQualification(null);
      }
      setStep(5);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors du chargement des spécialistes.");
    } finally {
      setLoading(false);
    }
  };

  // Basculement anti-impasse : re-fetch en pool=referenced en conservant tous les filtres
  const switchToReferencedPool = async () => {
    setPickedExpert(null);
    await loadRecipients("referenced");
  };

  // Audit modal state (Cas n°1 — spécialiste non abonné)
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  // STEP 5 → 6 : transmission au cabinet sélectionné — VERROU TRANSMETTRE
  // - Spécialiste abonné Confilex → transmission gratuite (comportement existant)
  // - Spécialiste NON abonné       → ouvre le sélecteur d'audit (29/39/49€), puis redirige Stripe
  // Le bouton "Transmettre" reste désactivé tant que tous les verrous ne sont pas levés.
  const runTransmit = async () => {
    if (!caseData?.id || !pickedExpert) return;
    if (!pickedExpert.is_subscribed) {
      // PAIEMENT 89€ OBLIGATOIRE AVANT TOUTE COMMUNICATION au profil non-abonné
      setError(null);
      setAuditOpen(true);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/dossier/${caseData.id}/transmit`, {
        expert_slug: pickedExpert.slug || pickedExpert.id,
        user_name: userInfo.name || undefined,
        user_email: userInfo.email || undefined,
        user_phone: userInfo.phone || undefined,
      });
      setTransmission(r.data);
      setStep(6);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de la transmission.");
    } finally {
      setLoading(false);
    }
  };

  // Confirmation niveau d'audit → checkout Stripe (29/39/49€) OU bypass ADMIN_TEST
  const onAuditConfirm = async (level) => {
    if (!caseData?.id || !pickedExpert) return;
    setError(null);
    setAuditLoading(true);
    try {
      const origin = window.location.origin;
      const r = await axios.post(`${API}/dossier/${caseData.id}/audit/checkout`, {
        level,
        expert_slug: pickedExpert.slug || pickedExpert.id,
        origin_url: origin,
        consent_accepted: true,
        waiver_14_days_accepted: true,
        customer_email: userInfo.email || undefined,
        user_name: userInfo.name || undefined,
        user_phone: userInfo.phone || undefined,
      });
      // Priorité 1 : Stripe checkout_url (nouvelle commande à payer)
      // Priorité 2 : redirect_url (déjà payé/ADMIN_TEST — vers page de suivi)
      // Priorité 3 : url (legacy alias)
      const redirect = r.data?.checkout_url || r.data?.redirect_url || r.data?.url;
      if (redirect) {
        window.location.href = redirect;
      } else if (r.data?.audit_id) {
        // Cas de secours : on connaît l'audit mais pas d'URL — on route côté client
        window.location.href = `/dossier/${caseData.id}/audit/success?audit_id=${r.data.audit_id}${r.data.payment_status === "ADMIN_TEST" ? "&admin_test=1" : ""}`;
      } else {
        setError("Impossible de créer la session de paiement.");
        setAuditLoading(false);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de la création du paiement.");
      setAuditLoading(false);
    }
  };

  // Checkout audit autonome (étape 4 — services complémentaires, avant choix spécialiste)
  // L'utilisateur achète un audit approfondi pour enrichir son dossier IA SANS
  // qu'un spécialiste ne soit encore sélectionné. Le retour Stripe pointe vers
  // /dossier/{caseId}/audit/success qui marque le dossier comme enrichi.
  const onAuditCheckoutStandalone = async (level) => {
    if (!caseData?.id) return;
    setError(null);
    setAuditLoading(true);
    try {
      const origin = window.location.origin;
      const r = await axios.post(`${API}/dossier/${caseData.id}/audit/checkout`, {
        level,
        expert_slug: undefined, // pas de spécialiste encore choisi
        origin_url: origin,
        consent_accepted: true,
        waiver_14_days_accepted: true,
        customer_email: userInfo.email || undefined,
        user_name: userInfo.name || undefined,
        user_phone: userInfo.phone || undefined,
      });
      const redirect = r.data?.checkout_url || r.data?.redirect_url || r.data?.url;
      if (redirect) {
        window.location.href = redirect;
      } else if (r.data?.audit_id) {
        window.location.href = `/dossier/${caseData.id}/audit/success?audit_id=${r.data.audit_id}${r.data.payment_status === "ADMIN_TEST" ? "&admin_test=1" : ""}`;
      } else {
        setError("Impossible de créer la session de paiement.");
        setAuditLoading(false);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de la création du paiement.");
      setAuditLoading(false);
    }
  };

  // Validation par étape — VERROU PARCOURS UNIQUE
  // Seuil de 60 caractères aligné avec la qualification-gate backend (dossier_ai.py)
  const canProceed = useMemo(() => {
    if (step === 1) return description.trim().length >= MIN_DESCRIPTION_CHARS;
    if (step === 2) return true; // documents optionnels
    if (step === 3) return !!caseData;
    if (step === 4) return complementaryServiceChosen; // verrou services complémentaires
    if (step === 5) return !!pickedExpert && userInfo.name.trim() && userInfo.email.trim();
    return false;
  }, [step, description, caseData, complementaryServiceChosen, pickedExpert, userInfo]);

  return (
    <div className="cb-shell" data-testid="dossier-wizard">
      <Seo
        title="Préparez votre dossier avec l'IA · Confilex"
        description="Décrivez votre situation, importez vos pièces, recevez un dossier structuré par l'IA Confilex et transmettez-le au spécialiste adapté en quelques minutes."
        canonical={typeof window !== "undefined" ? window.location.origin + "/dossier" : undefined}
      />

      <BookingNavbar />

      {/* HEADER */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pt-10 lg:pt-16 pb-6">
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] cb-gold font-semibold mb-3">
          <Sparkles className="w-3.5 h-3.5" /> Préparation IA Confilex
        </span>
        <h1 className="cb-serif text-3xl sm:text-4xl lg:text-5xl font-semibold cb-text-ivory leading-tight">
          Préparez votre <span className="cb-gold">dossier</span> en quelques minutes
        </h1>
        <p className="text-[#BFC8C2] mt-4 max-w-2xl">
          L'IA Confilex structure votre situation : chronologie · résumé · pièces manquantes · points de vigilance.
          Vous transmettez ensuite un dossier clair, qualifié et prêt à être exploité.
        </p>
      </section>

      {/* STEPPER */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-2">
        <ol className="flex items-center justify-between gap-2 overflow-x-auto" data-testid="stepper">
          {STEPS.map((s, i) => {
            const done = step > s.n;
            const active = step === s.n;
            return (
              <li key={s.n} className="flex items-center gap-2 flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all"
                    style={
                      done ? { background: "#2ECC71", color: "#031B12" }
                      : active ? { background: "#F5C400", color: "#031B12", boxShadow: "0 0 18px rgba(245,196,0,0.4)" }
                      : { background: "var(--cb-surface)", color: "var(--cb-muted)", border: "1px solid var(--cb-surface-2)" }
                    }
                  >
                    {done ? <Check className="w-4 h-4" /> : s.n}
                  </span>
                  <span className={`text-xs lg:text-sm font-medium truncate ${active ? "cb-text-ivory" : done ? "text-[#2ECC71]" : "text-[#BFC8C2]/70"}`}>
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-px bg-[var(--cb-surface-2)] mx-2" />
                )}
              </li>
            );
          })}
        </ol>
      </section>

      {/* CONTENT */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-8 lg:py-10">
        {error && (
          <div className="mb-5 p-4 rounded-lg flex items-center gap-3 border border-red-500/40 bg-red-500/10 text-red-200 text-sm"
               data-testid="wizard-error">
            <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {step === 1 && (
          <Step1Description
            description={description} setDescription={setDescription}
            domaine={domaine} setDomaine={setDomaine}
            city={city} setCity={setCity}
          />
        )}
        {step === 2 && (
          <Step2Documents
            documents={documents} setDocuments={setDocuments}
            onSkip={runAnalyze}
          />
        )}
        {step === 3 && (
          <Step3Analysis caseData={caseData} loading={loading} />
        )}
        {step === 4 && (
          <Step4ComplementaryServices
            caseData={caseData}
            chosen={complementaryService}
            onChoose={setComplementaryService}
            onPayAudit={(level) => onAuditCheckoutStandalone(level)}
            auditLoading={auditLoading}
          />
        )}
        {step === 5 && (
          <Step5SelectExpert
            recipients={recipients} loading={loading}
            qualification={qualification}
            expertPool={expertPool}
            poolHasFallback={poolHasFallback}
            onSwitchToReferenced={switchToReferencedPool}
            onEditDescription={() => setStep(1)}
            picked={pickedExpert} onPick={setPickedExpert}
            userInfo={userInfo} setUserInfo={setUserInfo}
            caseData={caseData}
            documents={documents} setDocuments={setDocuments}
          />
        )}
        {step === 6 && (
          <Step6Confirmation transmission={transmission} caseData={caseData} pickedExpert={pickedExpert} />
        )}

        {/* NAV */}
        {step < 6 && (
          <div className="mt-10 flex items-center justify-between gap-3">
            <button
              onClick={goBack}
              disabled={step === 1}
              className="cb-btn-ghost inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="wizard-back"
            >
              <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <button
              onClick={() => {
                if (step === 1) goNext();
                else if (step === 2) runAnalyze();
                else if (step === 3) goToComplementary();
                else if (step === 4) loadRecipients();
                else if (step === 5) runTransmit();
              }}
              disabled={!canProceed || loading}
              className="cb-btn-gold inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="wizard-next"
            >
              {loading ? "Traitement…" : (
                <>
                  {step === 1 && "Continuer"}
                  {step === 2 && "Lancer l'analyse IA"}
                  {step === 3 && "Voir les services complémentaires"}
                  {step === 4 && "Voir mes 3 spécialistes recommandés"}
                  {step === 5 && (
                    pickedExpert && !pickedExpert.is_subscribed
                      ? "Débloquer + transmettre (89 €)"
                      : "Transmettre le dossier"
                  )}
                  {step === 5 ? <Send className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                </>
              )}
            </button>
          </div>
        )}

        {step === 6 && (
          <div className="mt-10 flex justify-center">
            <button onClick={() => nav("/")} className="cb-btn-ghost text-sm">
              Retour à l'accueil
            </button>
          </div>
        )}
      </section>

      <AuditLevelSelector
        open={auditOpen}
        onClose={() => !auditLoading && setAuditOpen(false)}
        onConfirm={onAuditConfirm}
        flow="dossier"
        expertCabinet={pickedExpert?.cabinet}
        loading={auditLoading}
      />

      <Footer />
    </div>
  );
}


/* ============================================================
   STEP COMPONENTS
   ============================================================ */

const MIN_DESCRIPTION_CHARS = 60;

// Exemples pour aider l'utilisateur qui bloque sur la longueur
const DESCRIPTION_EXAMPLES = [
  "J'ai été licencié le 12 février pour faute grave alors que j'avais 8 ans d'ancienneté. Aucun entretien préalable.",
  "Mon bailleur refuse de me rendre ma caution de 1 200 € depuis 3 mois sans justification, malgré 2 mises en demeure.",
  "J'ai créé une SAS en octobre. Mon associé souhaite sortir mais nous n'avons pas de pacte d'associés signé.",
];

function Step1Description({ description, setDescription, domaine, setDomaine, city, setCity }) {
  const len = description.trim().length;
  const rawLen = description.length;
  const isValid = len >= MIN_DESCRIPTION_CHARS;
  const progress = Math.min(100, Math.round((len / MIN_DESCRIPTION_CHARS) * 100));
  return (
    <div className="cb-card p-7 lg:p-8 space-y-6" data-testid="step-1">
      <div>
        <label className="block cb-text-ivory font-semibold mb-2">
          1. Décrivez votre situation en quelques mots
        </label>
        <p className="text-xs text-[#BFC8C2] mb-3">
          Soyez factuel — ne vous censurez pas. Plus votre récit est précis, plus l'IA
          Confilex saura identifier le spécialiste adapté. Objectif : au moins {MIN_DESCRIPTION_CHARS} caractères.
        </p>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={7}
          placeholder="Exemple : J'ai été licencié le 12 février pour faute grave alors que j'avais 8 ans d'ancienneté. Mon employeur m'a remis une lettre de licenciement sans entretien préalable…"
          className="w-full bg-[#031B12] border border-[#063D25] rounded-lg p-4 text-sm cb-text-ivory placeholder:text-[#BFC8C2]/50 focus:border-[#F5C400]/60 outline-none resize-y"
          data-testid="step1-description"
        />
        {/* Barre de progression + compteur dynamique */}
        <div className="mt-2 space-y-1.5" data-testid="step1-description-counter">
          <div className="h-1.5 bg-[#063D25] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${isValid ? "bg-[#2ECC71]" : "bg-[#F5C400]"}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between items-center text-[11px]">
            <span className={isValid ? "text-[#2ECC71] font-medium" : "text-[#BFC8C2]/80"}>
              {isValid
                ? `✓ Description suffisante (${len} caractères)`
                : `${len} / ${MIN_DESCRIPTION_CHARS} caractères — encore ${MIN_DESCRIPTION_CHARS - len} pour continuer`}
            </span>
            <span className="text-[#BFC8C2]/60">{rawLen}/5000</span>
          </div>
        </div>
        {/* Exemples cliquables si description trop courte */}
        {!isValid && len < 40 && (
          <div className="mt-4 rounded-lg bg-[#031B12]/60 border border-[#063D25] p-3">
            <p className="text-[11px] text-[#BFC8C2]/80 mb-2 uppercase tracking-widest font-medium">
              Besoin d'inspiration ? Cliquez sur un exemple pour l'utiliser
            </p>
            <div className="space-y-1.5">
              {DESCRIPTION_EXAMPLES.map((ex, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setDescription(ex)}
                  className="w-full text-left text-xs text-[#BFC8C2] hover:text-[#F5C400] hover:bg-[#063D25]/40 rounded px-2 py-1.5 transition"
                  data-testid={`step1-example-${i}`}
                >
                  « {ex} »
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="block cb-text-ivory font-semibold mb-2 text-sm">
            Domaine (optionnel)
          </label>
          <select
            value={domaine}
            onChange={(e) => setDomaine(e.target.value)}
            className="w-full bg-[#031B12] border border-[#063D25] rounded-lg p-3 text-sm cb-text-ivory focus:border-[#F5C400]/60 outline-none"
            data-testid="step1-domaine"
          >
            <option value="">— L'IA décide pour moi —</option>
            {DOMAINES.map((d) => (
              <option key={d} value={d} className="bg-[#031B12]">{d}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block cb-text-ivory font-semibold mb-2 text-sm">
            Ville préférée (optionnel)
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 cb-gold" />
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ex: Paris, Lyon, Marseille…"
              className="w-full pl-9 pr-3 py-3 bg-[#031B12] border border-[#063D25] rounded-lg text-sm cb-text-ivory placeholder:text-[#BFC8C2]/50 focus:border-[#F5C400]/60 outline-none"
              data-testid="step1-city"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Step2Documents({ documents, setDocuments, onSkip }) {
  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []).slice(0, 10);
    const docs = files.map((f) => ({ name: f.name, size: f.size, type: f.type }));
    setDocuments((prev) => [...prev, ...docs].slice(0, 10));
  };

  const removeAt = (i) => setDocuments((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <div className="cb-card p-7 lg:p-8" data-testid="step-2">
      <h2 className="cb-text-ivory font-semibold mb-2">
        2. Vos pièces (optionnel, à tout moment)
      </h2>
      <p className="text-xs text-[#BFC8C2] mb-5">
        Vous pouvez ajouter vos pièces maintenant, plus tard depuis votre espace,
        ou juste avant la transmission au spécialiste. Aucune pièce n'est envoyée
        tant que vous n'avez pas validé explicitement la transmission.
      </p>

      <label
        className="block border-2 border-dashed border-[#063D25] hover:border-[#F5C400]/60 rounded-xl p-10 text-center cursor-pointer transition-colors"
        data-testid="step2-dropzone"
      >
        <input
          type="file"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="sr-only"
          data-testid="step2-file-input"
        />
        <Upload className="w-8 h-8 cb-gold mx-auto mb-3" strokeWidth={1.5} />
        <p className="cb-text-ivory text-sm font-medium">
          Ajouter mes pièces maintenant
        </p>
        <p className="text-xs text-[#BFC8C2] mt-1">
          PDF, Word, images. 10 fichiers max.
        </p>
      </label>

      {documents.length > 0 && (
        <ul className="mt-6 space-y-2" data-testid="step2-doc-list">
          {documents.map((d, i) => (
            <li key={i}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#031B12] border border-[#063D25]">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 cb-gold shrink-0" />
                <span className="text-sm cb-text-ivory truncate">{d.name}</span>
                <span className="text-xs text-[#BFC8C2]/70 shrink-0">
                  {Math.round((d.size || 0) / 1024)} KB
                </span>
              </div>
              <button onClick={() => removeAt(i)} aria-label="Retirer"
                      className="p-1 text-[#BFC8C2] hover:text-red-400 transition">
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Skip explicite — deuxième chemin natural du parcours flexible */}
      <div className="mt-6 pt-6 border-t border-[#063D25] flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-[#BFC8C2]">
          {documents.length > 0
            ? `${documents.length} pièce${documents.length > 1 ? "s" : ""} ajoutée${documents.length > 1 ? "s" : ""}. Vous pouvez continuer.`
            : "Aucune pièce ? Vous pourrez les ajouter à tout moment."}
        </p>
        <button
          type="button"
          onClick={onSkip}
          className="text-xs cb-gold hover:underline"
          data-testid="step2-skip"
        >
          Je compléterai mon dossier plus tard →
        </button>
      </div>
    </div>
  );
}

function Step3Analysis({ caseData, loading }) {
  if (loading) return <AnalysisLoader />;
  if (!caseData) return null;

  const urgencyColor = caseData.urgency_level === "elevee"
    ? "#FF6B6B" : caseData.urgency_level === "moyenne"
    ? "#F5C400" : "#2ECC71";

  return (
    <div className="space-y-5" data-testid="step-3">
      <div className="cb-card p-7 lg:p-8 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl pointer-events-none"
             style={{ background: "rgba(245,196,0,0.16)" }} />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] cb-gold font-bold">
              <Sparkles className="w-3 h-3" /> Dossier structuré par l'IA
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: `${urgencyColor}22`, color: urgencyColor, border: `1px solid ${urgencyColor}44` }}>
              Urgence : {caseData.urgency_level}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#BFC8C2]/80 font-medium">
              · Complexité : {caseData.estimated_complexity}
            </span>
          </div>
          <h2 className="cb-serif text-2xl cb-text-ivory mt-2 mb-3">Résumé du dossier</h2>
          <p className="text-[#F7F7F2]/90 leading-relaxed" data-testid="step3-summary">
            {caseData.summary}
          </p>
          <p className="text-sm cb-gold mt-4">
            Spécialité recommandée : <strong>{caseData.suggested_specialty}</strong>
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Chronologie */}
        <Card icon={Clock} title="Chronologie">
          {caseData.timeline?.length > 0 ? (
            <ol className="space-y-3" data-testid="step3-timeline">
              {caseData.timeline.map((t, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="cb-gold font-bold text-xs shrink-0 w-20">{t.date || "—"}</span>
                  <span className="cb-text-ivory">{t.event}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-[#BFC8C2]/70">Aucun élément daté détecté dans votre description.</p>
          )}
        </Card>

        {/* Questions clés */}
        <Card icon={ListChecks} title="Questions juridiques identifiées">
          <ul className="space-y-2 text-sm" data-testid="step3-questions">
            {(caseData.key_questions || []).map((q, i) => (
              <li key={i} className="flex items-start gap-2 cb-text-ivory">
                <ChevronRight className="w-3.5 h-3.5 cb-gold shrink-0 mt-0.5" />
                {q}
              </li>
            ))}
          </ul>
        </Card>

        {/* Pièces manquantes */}
        <Card icon={FileText} title="Pièces manquantes à fournir">
          <ul className="space-y-2 text-sm" data-testid="step3-missing">
            {(caseData.missing_pieces || []).map((p, i) => (
              <li key={i} className="flex items-start gap-2 cb-text-ivory">
                <AlertTriangle className="w-3.5 h-3.5 cb-gold shrink-0 mt-0.5" />
                {p}
              </li>
            ))}
          </ul>
        </Card>

        {/* Vigilance */}
        <Card icon={AlertTriangle} title="Points de vigilance">
          <ul className="space-y-2 text-sm" data-testid="step3-vigilance">
            {(caseData.vigilance_points || []).map((p, i) => (
              <li key={i} className="flex items-start gap-2 cb-text-ivory">
                <ShieldCheck className="w-3.5 h-3.5 cb-gold shrink-0 mt-0.5" />
                {p}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

/* ============================================================
   STEP 4 — Services complémentaires (Parcours unique v3.43)
   ============================================================
   L'utilisateur choisit OBLIGATOIREMENT une option avant d'accéder
   à la sélection de spécialiste (étape 5) :
     · Poursuite gratuite     → le dossier IA est suffisant
     · Audit Essentiel 29 €   → enrichissement documentaire ciblé
     · Audit Avancé 39 €      → enrichissement étendu + risques
     · Audit Premium 49 €     → enrichissement complet + stratégie
   Le bouton "Voir mes 3 spécialistes recommandés" reste désactivé tant
   qu'aucune option n'a été choisie (verrou structurel).
   ============================================================ */
function Step4ComplementaryServices({ caseData, chosen, onChoose, onPayAudit, auditLoading }) {
  const audits = [
    { level: "essentiel", price: 29, name: "Audit Essentiel",
      points: ["Inventaire approfondi des pièces", "Synthèse rédactionnelle structurée", "Questions à poser au spécialiste"] },
    { level: "avance",    price: 39, name: "Audit Avancé",
      points: ["Tout l'Audit Essentiel", "Détection des risques juridiques élevés", "Cartographie chronologique enrichie"] },
    { level: "premium",   price: 49, name: "Audit Premium",
      points: ["Tout l'Audit Avancé", "Stratégie de résolution amiable/contentieuse", "Pré-rédaction de courriers types"] },
  ];

  return (
    <div className="space-y-5" data-testid="step-4">
      <div className="cb-card p-7 lg:p-8">
        <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] cb-gold font-bold">
          <Sparkles className="w-3 h-3" /> Étape 4 · Enrichir votre dossier (optionnel)
        </span>
        <h2 className="cb-serif text-2xl lg:text-3xl cb-text-ivory font-semibold mt-2 mb-3">
          Votre dossier IA est prêt — souhaitez-vous l'enrichir avant transmission&nbsp;?
        </h2>
        <p className="text-sm text-[#BFC8C2] max-w-2xl">
          Vous pouvez transmettre votre dossier IA tel quel à un expert (gratuit) ou commander
          un <span className="cb-gold font-semibold">audit approfondi CONFILEX</span> pour
          renforcer la qualité de la lecture avant transmission. Ce choix est obligatoire
          pour accéder à la sélection des 3 spécialistes les plus adaptés.
        </p>
      </div>

      {/* OPTION 1 — Poursuite gratuite */}
      <button
        type="button"
        onClick={() => onChoose("free")}
        className="w-full text-left cb-card p-6 lg:p-7 transition relative flex items-start gap-4 hover:border-[#F5C400]/60"
        style={chosen === "free" ? { borderColor: "#F5C400", boxShadow: "0 0 0 1px #F5C400" } : {}}
        data-testid="complementary-option-free"
      >
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
             style={{ background: "rgba(46,204,113,0.18)", border: "1px solid #2ECC71" }}>
          <CheckCircle2 className="w-5 h-5" style={{ color: "#2ECC71" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="cb-text-ivory font-semibold">Poursuivre gratuitement</h3>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-[#2ECC71] text-[#031B12]">
              0 €
            </span>
          </div>
          <p className="text-sm text-[#BFC8C2] mt-1">
            Le dossier IA structuré (résumé · chronologie · pièces manquantes · vigilances) est transmis tel quel à l'expert sélectionné.
          </p>
        </div>
        {chosen === "free" && (
          <Check className="w-5 h-5 cb-gold shrink-0 mt-1" />
        )}
      </button>

      {/* OPTIONS 2-4 — Audits approfondis */}
      <div className="grid md:grid-cols-3 gap-4">
        {audits.map((a) => {
          const key = `audit_${a.level}`;
          const isChosen = chosen === key;
          return (
            <div
              key={a.level}
              className="cb-card p-5 transition flex flex-col relative"
              style={isChosen ? { borderColor: "#F5C400", boxShadow: "0 0 0 1px #F5C400" } : {}}
              data-testid={`complementary-option-${a.level}`}
            >
              <div className="flex items-baseline gap-2 mb-1">
                <span className="cb-serif text-2xl cb-gold font-semibold">{a.price} €</span>
                <span className="text-xs text-[#BFC8C2]">TTC</span>
              </div>
              <h4 className="cb-text-ivory font-semibold mb-3">{a.name}</h4>
              <ul className="space-y-1.5 text-xs text-[#BFC8C2] mb-5 flex-1">
                {a.points.map((p) => (
                  <li key={p} className="flex items-start gap-1.5">
                    <Check className="w-3 h-3 cb-gold shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => { onChoose(key); onPayAudit(a.level); }}
                disabled={auditLoading}
                className="cb-btn-gold text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid={`complementary-pay-${a.level}`}
              >
                {auditLoading && isChosen ? "Redirection Stripe…" : `Choisir & payer ${a.price} €`}
              </button>
            </div>
          );
        })}
      </div>

      {/* Indication verrou */}
      {!chosen && (
        <p className="text-xs text-[#BFC8C2]/70 text-center pt-3 inline-flex items-center justify-center gap-1.5 w-full"
           data-testid="complementary-locked-hint">
          <ShieldCheck className="w-3.5 h-3.5 cb-gold" />
          Choisissez une option ci-dessus pour débloquer la sélection des spécialistes.
        </p>
      )}
    </div>
  );
}



function Step5SelectExpert({ recipients, loading, qualification, expertPool, poolHasFallback, onSwitchToReferenced, onEditDescription, picked, onPick, userInfo, setUserInfo, caseData, documents, setDocuments }) {
  // v3.44 — Basculement anti-impasse entre "abonnés Confilex" et "référencés"
  // v3.43 — Hard cap visuel à 3 cards + score de compatibilité + masquage strict
  // des coordonnées pour les non-abonnés (déjà appliqué côté backend en v3.39).
  const top3 = (recipients || []).slice(0, 3);
  const isSubscribedPool = expertPool === "subscribed";
  const isReferencedPool = expertPool === "referenced";
  const MISSING_LABELS = {
    domaine_juridique: "le domaine juridique",
    localisation: "la ville",
    description_situation: "une description détaillée (≥ 60 caractères)",
    niveau_urgence: "le niveau d'urgence",
    synthese_ia: "la synthèse IA du dossier",
  };
  return (
    <div className="space-y-5" data-testid="step-5">
      <div className="cb-card p-7 lg:p-8">
        <span className="text-[10px] uppercase tracking-[0.22em] cb-gold font-semibold">
          Sélection IA · {caseData?.suggested_specialty || "votre domaine"}
        </span>
        <h2 className="cb-serif text-2xl lg:text-3xl cb-text-ivory font-semibold mt-2 mb-2">
          {isReferencedPool
            ? "Nos professionnels référencés adaptés à votre situation"
            : "Les 3 spécialistes les plus adaptés à votre situation"}
        </h2>
        <p className="text-xs text-[#BFC8C2] mb-5 max-w-2xl">
          {isReferencedPool ? (
            <>
              Vous consultez actuellement les <span className="cb-gold font-semibold">professionnels référencés</span> issus
              de notre base publique. La mise en relation nécessite un audit préalable (29 · 39 · 49 €)
              qui débloque leurs coordonnées et transmet automatiquement votre dossier IA structuré.
            </>
          ) : (
            <>
              Sélection automatique parmi nos <span className="cb-gold font-semibold">Spécialistes abonnés Confilex</span> —
              contact et transmission du dossier <span className="cb-gold font-semibold">gratuits</span>.
            </>
          )}
        </p>

        {loading ? (
          <div className="grid sm:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="cb-card p-5 animate-pulse h-44 bg-[#063D25]/40" />
            ))}
          </div>
        ) : qualification?.required ? (
          <div className="cb-card p-5 border border-[#F5C400]/40 bg-[#F5C400]/5" data-testid="qualification-required">
            <p className="text-sm cb-text-ivory font-semibold mb-2">
              Votre dossier n'est pas encore assez précis
            </p>
            <p className="text-sm text-[#BFC8C2] mb-3">
              {qualification.guidance} Pour identifier les spécialistes les plus adaptés,
              il nous manque :
            </p>
            <ul className="text-sm text-[#BFC8C2] list-disc pl-5 space-y-1 mb-4">
              {(qualification.missing || []).map((m) => (
                <li key={m}>
                  <span className="cb-gold">{MISSING_LABELS[m] || m}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={onEditDescription}
              className="cb-btn-gold inline-flex items-center gap-2"
              data-testid="btn-edit-description"
            >
              <ArrowLeft className="w-4 h-4" />
              Compléter ma description
            </button>
            <p className="text-xs text-[#BFC8C2]/70 mt-3 italic">
              Vos informations déjà saisies sont conservées — vous pouvez enrichir votre récit sans repartir de zéro.
            </p>
          </div>
        ) : top3.length === 0 && isSubscribedPool && poolHasFallback ? (
          // 🎯 Anti-impasse : aucun abonné Confilex mais des référencés existent
          <div
            className="cb-card p-6 border border-[#C9A961]/40 bg-[#0B3A2A]/50"
            data-testid="pool-fallback-card"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: "rgba(201,169,97,0.18)", border: "1px solid #C9A961" }}>
                <Sparkles className="w-5 h-5 cb-gold" />
              </div>
              <div>
                <h3 className="cb-text-ivory font-semibold mb-1.5">
                  Aucun spécialiste abonné Confilex n'est actuellement disponible pour votre recherche
                </h3>
                <p className="text-sm text-[#BFC8C2]">
                  Vous pouvez néanmoins accéder à notre <span className="cb-gold font-semibold">réseau
                  de professionnels référencés</span> afin de poursuivre votre démarche. Nous
                  conservons tous vos critères (spécialité, ville, urgence, langue).
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onSwitchToReferenced}
              className="cb-btn-gold inline-flex items-center gap-2 w-full sm:w-auto justify-center"
              data-testid="switch-to-referenced-btn"
            >
              Voir les professionnels référencés <ArrowRight className="w-4 h-4" />
            </button>

            <p className="text-xs text-[#BFC8C2]/80 mt-4 leading-relaxed">
              La mise en relation avec un professionnel référencé est un <span className="cb-gold">service payant</span> (29 · 39 · 49 € TTC selon
              le niveau d'audit choisi). Les conditions tarifaires détaillées vous seront
              présentées <strong>avant toute validation</strong>.
            </p>
          </div>
        ) : top3.length === 0 ? (
          <div className="cb-card p-5 border border-[#BFC8C2]/20" data-testid="no-match-found">
            <p className="text-sm text-[#BFC8C2] mb-3">
              Aucun professionnel ne correspond à votre dossier pour la spécialité
              <span className="cb-gold"> « {caseData?.suggested_specialty || "demandée"} »</span>
              {caseData?.city && <> dans la région de <span className="cb-gold">{caseData.city}</span></>}.
            </p>
            <p className="text-xs text-[#BFC8C2]/80 italic">
              Essayez d'élargir la zone géographique ou précisez la spécialité à l'étape précédente.
            </p>
          </div>
        ) : (
          <ul className="grid sm:grid-cols-3 gap-4" data-testid="recipients-top3">
            {top3.map((r) => {
              const checked = picked?.id === r.id;
              const score = r.compatibility_score ?? 0;
              const isSub = !!r.is_subscribed;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => onPick(r)}
                    className="w-full h-full text-left cb-card p-5 hover:border-[#F5C400]/60 transition relative flex flex-col"
                    style={checked ? { borderColor: "#F5C400", boxShadow: "0 0 0 1px #F5C400" } : {}}
                    data-testid={`recipient-${r.id}`}
                  >
                    {/* Compatibility score badge (toujours en haut à droite) */}
                    <span
                      className="absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F5C400] text-[#031B12]"
                      data-testid={`recipient-score-${r.id}`}
                    >
                      {score}% compatible
                    </span>

                    {checked && (
                      <span className="absolute top-3 left-3 w-6 h-6 rounded-full bg-[#F5C400] flex items-center justify-center">
                        <Check className="w-3 h-3 text-[#031B12]" />
                      </span>
                    )}

                    <div className="mt-7 mb-2 flex items-center gap-2 flex-wrap">
                      <Building2 className="w-4 h-4 cb-gold" />
                      <span className="cb-text-ivory font-semibold">
                        {isSub ? (r.cabinet || "Cabinet abonné Confilex")
                              : "Profil verrouillé"}
                      </span>
                    </div>

                    {isSub ? (
                      <span
                        data-testid={`recipient-badge-subscribed-${r.id}`}
                        className="self-start text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#2ECC71] text-[#031B12] mb-3"
                      >
                        Abonné Confilex
                      </span>
                    ) : (
                      <span
                        data-testid={`recipient-badge-referenced-${r.id}`}
                        className="self-start text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border border-[#F5C400]/60 cb-gold mb-3"
                      >
                        Audit 89 € pour débloquer
                      </span>
                    )}

                    <p className="text-xs text-[#BFC8C2] mb-3">
                      <MapPin className="w-3 h-3 inline-block mr-1" />
                      {r.city}{r.department ? ` · Dépt. ${r.department}` : ""}
                    </p>

                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(r.practice_areas || []).slice(0, 3).map((p) => (
                        <span key={p} className="text-[10px] px-2 py-0.5 rounded-lg border border-[#063D25] cb-text-ivory">
                          {p}
                        </span>
                      ))}
                    </div>

                    {isSub && (r.phone || r.website) && (
                      <div className="mt-auto pt-2 border-t border-[#063D25] flex items-center gap-3 text-xs text-[#BFC8C2]">
                        {r.phone && (
                          <span className="inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {r.phone}
                          </span>
                        )}
                        {r.website && (
                          <span className="inline-flex items-center gap-1">
                            <Globe className="w-3 h-3" /> Site web
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Coordonnées utilisateur */}
      <div className="cb-card p-7 lg:p-8">
        <h3 className="cb-text-ivory font-semibold mb-4">Vos coordonnées</h3>
        <p className="text-xs text-[#BFC8C2] mb-4">
          Le spécialiste reçoit votre dossier structuré + ces coordonnées pour vous recontacter.
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          <FieldInput icon={User} label="Nom complet" value={userInfo.name}
                      onChange={(v) => setUserInfo({ ...userInfo, name: v })}
                      testid="userinfo-name" required />
          <FieldInput icon={Mail} label="Email" type="email" value={userInfo.email}
                      onChange={(v) => setUserInfo({ ...userInfo, email: v })}
                      testid="userinfo-email" required />
          <FieldInput icon={Phone} label="Téléphone (optionnel)" type="tel" value={userInfo.phone}
                      onChange={(v) => setUserInfo({ ...userInfo, phone: v })}
                      testid="userinfo-phone" />
        </div>

        {/* Panneau "pièces à joindre" — dépôt flexible AVANT transmission */}
        <div className="mt-6 pt-6 border-t border-[#063D25]" data-testid="step5-attach-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div>
              <h4 className="cb-text-ivory font-semibold text-sm mb-1">
                Pièces à joindre au dossier
              </h4>
              <p className="text-xs text-[#BFC8C2]">
                {(documents || []).length > 0
                  ? `${documents.length} pièce${documents.length > 1 ? "s" : ""} prête${documents.length > 1 ? "s" : ""} à transmettre.`
                  : "Aucune pièce ajoutée — vous pouvez en ajouter maintenant ou transmettre sans pièces."}
              </p>
            </div>
            <label
              className="inline-flex items-center gap-2 text-xs cb-gold hover:underline cursor-pointer"
              data-testid="step5-attach-more"
            >
              <input
                type="file" multiple className="sr-only"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []).slice(0, 10);
                  const docs = files.map((f) => ({ name: f.name, size: f.size, type: f.type }));
                  setDocuments((prev) => [...prev, ...docs].slice(0, 10));
                }}
              />
              <Upload className="w-3.5 h-3.5" /> Ajouter des pièces maintenant
            </label>
          </div>
          {(documents || []).length > 0 && (
            <ul className="space-y-1.5" data-testid="step5-doc-list">
              {documents.map((d, i) => (
                <li key={i}
                    className="flex items-center gap-2 text-xs p-2 rounded bg-[#031B12] border border-[#063D25]">
                  <FileText className="w-3.5 h-3.5 cb-gold shrink-0" />
                  <span className="cb-text-ivory truncate flex-1">{d.name}</span>
                  <button
                    type="button"
                    onClick={() => setDocuments((prev) => prev.filter((_, idx) => idx !== i))}
                    className="text-[#BFC8C2] hover:text-red-400"
                    aria-label="Retirer">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Note validation explicite */}
        <p className="text-[10px] text-[#BFC8C2]/70 mt-4 flex items-center gap-1.5"
           data-testid="step5-explicit-consent">
          <ShieldCheck className="w-3 h-3 cb-gold shrink-0" />
          Aucune pièce n'est envoyée tant que vous n'avez pas cliqué sur « Transmettre le dossier ».
        </p>
      </div>
    </div>
  );
}

function Step6Confirmation({ transmission, caseData, pickedExpert }) {
  return (
    <div className="cb-card p-8 lg:p-10 text-center" data-testid="step-6">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5 cb-fade-up"
           style={{ background: "rgba(46,204,113,0.18)", border: "1px solid #2ECC71" }}>
        <CheckCircle2 className="w-8 h-8" style={{ color: "#2ECC71" }} />
      </div>
      <h2 className="cb-serif text-3xl cb-text-ivory mb-3">
        Votre dossier a été transmis
      </h2>
      <p className="text-[#BFC8C2] max-w-xl mx-auto">
        Votre dossier structuré est désormais entre les mains de
        <strong className="cb-gold mx-1">{pickedExpert?.cabinet || transmission?.expert_cabinet}</strong>.
        Le cabinet reviendra vers vous sous quelques jours ouvrables.
      </p>

      <div className="mt-7 p-5 rounded-lg cb-bg-surface text-sm text-left max-w-xl mx-auto">
        <p className="cb-text-ivory font-semibold mb-2">Ce qui a été transmis :</p>
        <ul className="space-y-1.5 text-[#BFC8C2]">
          <li>• Résumé structuré · {caseData?.summary?.slice(0, 80)}…</li>
          <li>• {caseData?.timeline?.length || 0} événements datés</li>
          <li>• {caseData?.key_questions?.length || 0} questions juridiques identifiées</li>
          <li>• {caseData?.missing_pieces?.length || 0} pièces manquantes signalées</li>
          <li>• Spécialité ciblée · {caseData?.suggested_specialty}</li>
        </ul>
      </div>

      <p className="text-xs text-[#BFC8C2]/70 mt-6">
        Réf. transmission : <code className="cb-gold">{transmission?.transmission_id}</code>
      </p>
    </div>
  );
}


/* ============================================================
   UI HELPERS
   ============================================================ */

function Card({ icon: Icon, title, children }) {
  return (
    <div className="cb-card p-6">
      <h3 className="cb-text-ivory font-semibold flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 cb-gold" /> {title}
      </h3>
      {children}
    </div>
  );
}

function FieldInput({ icon: Icon, label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#BFC8C2] mb-1.5">
        {label} {required && <span className="cb-gold">*</span>}
      </span>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 cb-gold" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 bg-[#031B12] border border-[#063D25] rounded-lg text-sm cb-text-ivory focus:border-[#F5C400]/60 outline-none"
          data-testid={testid}
        />
      </div>
    </label>
  );
}

function AnalysisLoader() {
  const phases = [
    "Lecture de votre description…",
    "Identification du domaine juridique…",
    "Structuration de la chronologie…",
    "Détection des pièces manquantes…",
    "Analyse des points de vigilance…",
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % phases.length), 1400);
    return () => clearInterval(id);
  }, [phases.length]);
  return (
    <div className="cb-card p-10 lg:p-12 text-center" data-testid="analysis-loader">
      <Sparkles className="w-10 h-10 cb-gold mx-auto animate-pulse" />
      <h2 className="cb-serif text-2xl cb-text-ivory mt-5">L'IA Confilex structure votre dossier</h2>
      <p className="text-sm cb-gold mt-2 transition-opacity">{phases[idx]}</p>
      <div className="mt-6 mx-auto max-w-md flex items-center justify-center gap-2">
        {phases.map((_, i) => (
          <span key={i} className="h-1.5 flex-1 rounded-full transition-all"
                style={{ background: i <= idx ? "#F5C400" : "var(--cb-surface-2)" }} />
        ))}
      </div>
    </div>
  );
}
